import React, { useEffect, useCallback, useMemo, memo } from 'react';

import { useCurrentUser } from 'Auth';
import { DataCacheProvider } from 'api/DataCache';
import difference from 'lodash/difference';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import smoothScrollIntoView from 'smooth-scroll-into-view-if-needed';

import {
  useOrderedNodes,
  withNodeManager,
  View,
  GridWidgets,
} from '@gogame/components';
import {
  useMountedState,
  usePrevious,
  useProcessAsync,
} from '@gogame/hooks-ts';
import {
  SET_FILTERS,
  useScreenDispatch,
  useScreenState,
} from '@gogame/screens';

import { VALUE_PRESETS_PERIOD } from 'scenes/HomeDashboard/constants';
import { useLoadGridPresets } from 'scenes/HomeDashboard/hooks';
import {
  SET_WIDGETS,
  DELETE_WIDGET,
  COLLAPSE_WIDGET,
} from 'scenes/HomeDashboard/screenReducer';
import { editPreset } from 'scenes/HomeDashboard/sources/presets';
import { widgetNames } from 'scenes/HomeDashboard/widgets';

import DraggableHandler from './DraggableHandler';
import LazyLoad from './LazyLoad';
import Widget from './Widget';

const HomeDashboardWidgets = withNodeManager()(() => {
  const dispatch = useScreenDispatch();
  const screenState = useScreenState();
  const user = useCurrentUser();

  const [loading, processAsync] = useProcessAsync();
  const [presets, setPresets] = useMountedState([]);

  const loadGridPresets = useLoadGridPresets(processAsync);

  const loadData = useCallback(async () => {
    const presets = await loadGridPresets({
      keys: widgetNames,
      ids: screenState.filters.presets,
    });

    const filteredPresets = screenState.filters.presets.filter((id) =>
      presets.find((preset) => preset.id === id)
    );

    if (!isEqual(filteredPresets, screenState.filters.presets)) {
      dispatch({
        type: SET_FILTERS,
        payload: {
          ...screenState.filters,
          presets: filteredPresets,
        },
      });
    }

    setPresets(presets);
  }, [screenState.filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEditWidget = useCallback(async (nextStatePreset) => {
    await editPreset(nextStatePreset);
    setPresets((presets) =>
      presets.map((preset) =>
        preset.id === nextStatePreset.id ? nextStatePreset : preset
      )
    );
  }, []);

  const handleDeleteWidget = useCallback((id) => {
    dispatch({
      type: DELETE_WIDGET,
      payload: id,
    });
  }, []);

  const handleCollapseWidget = useCallback(
    (id) => (state) => {
      dispatch({
        type: COLLAPSE_WIDGET,
        payload: state,
        id: id,
      });
    },
    []
  );

  const isDefaultPeriod = useMemo(
    () =>
      !screenState.filters.period ||
      isEqual(screenState.filters.period, VALUE_PRESETS_PERIOD),
    [screenState.filters.period]
  );

  const handleSetWidgets = useCallback((state) => {
    dispatch({
      type: SET_WIDGETS,
      payload: state,
    });
  }, []);

  const parentPreset = useMemo(
    () =>
      isDefaultPeriod
        ? null
        : {
            period: screenState.filters.period,
          },
    [isDefaultPeriod, screenState.filters.period]
  );

  const previousWidgets = usePrevious(
    flatten(screenState.widgets.map((row) => row.map(({ id }) => id)))
  );

  const newWidgetIds = useMemo(() => {
    const widgetIds = flatten(
      screenState.widgets.map((row) => row.map(({ id }) => id))
    );

    if (!previousWidgets) {
      return widgetIds;
    }
    return difference(widgetIds, previousWidgets);
  }, [screenState.widgets]);

  const ordered = useOrderedNodes();

  useEffect(() => {
    if (!isEmpty(ordered)) {
      smoothScrollIntoView(ordered[0], {
        scrollMode: 'if-needed',
        block: 'center',
        inline: 'start',
      });
    }
  }, [ordered]);

  return (
    <DataCacheProvider>
      <View
        loading={loading && isEmpty(presets)}
        transparent={true}
        flexGrow={1}
      >
        <GridWidgets
          state={screenState.widgets}
          onChange={handleSetWidgets}
          spacing={16}
        >
          {({ id, collapsed, firstRow, lastRow, mounted }) => (
            <LazyLoad mounted={mounted}>
              {({ active }) => {
                const preset = presets.find((preset) => preset.id === id);

                if (!preset) {
                  return null;
                }

                return (
                  <Widget
                    active={active && !collapsed}
                    id={id}
                    preset={preset}
                    isNew={newWidgetIds.includes(id)}
                    parentPreset={parentPreset}
                    globalPeriod={!isDefaultPeriod}
                    onDelete={handleDeleteWidget}
                    onEdit={handleEditWidget}
                    onCollapse={handleCollapseWidget(id)}
                    collapsed={collapsed}
                    editable={user.id === preset.user.id}
                    tooltipPosStrategy={lastRow && !firstRow ? 'top' : 'bottom'}
                    affix={
                      <GridWidgets.DraggableHandler id={id}>
                        <DraggableHandler />
                      </GridWidgets.DraggableHandler>
                    }
                  />
                );
              }}
            </LazyLoad>
          )}
        </GridWidgets>
      </View>
    </DataCacheProvider>
  );
});

export default memo(HomeDashboardWidgets, isEqual);
